import React, { useEffect, useState } from 'react';
import {
    Box, CircularProgress, Typography, Select, MenuItem,
    IconButton, makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { listFeaturesService } from '../../../services/feature.service';

const MODES = [
    { value: '', label: 'Not in plan' },
    { value: 'active', label: 'Active (on by default)' },
    { value: 'available', label: 'Available (activatable)' },
];

const CARD_STYLES = {
    active: { background: '#ecfdf5', border: '1px solid #6ee7b7', titleColor: '#047857' },
    available: { background: '#eff6ff', border: '1px solid #93c5fd', titleColor: '#1d4ed8' },
    default: { background: '#f9fafb', border: '1px solid #d1d5db', titleColor: '#374151' },
};

const useStyles = makeStyles((theme) => ({
    addBar: {
        marginBottom: theme.spacing(1.5),
    },
    addSelect: {
        width: '100%',
    },
    cardGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: theme.spacing(1.5),
    },
    card: {
        borderRadius: 6,
        padding: theme.spacing(1.25, 1.5),
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
    },
    cardHeader: {
        marginBottom: theme.spacing(1),
        paddingRight: theme.spacing(2.5),
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        lineHeight: 1.25,
    },
    cardKey: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
        marginTop: 1,
        wordBreak: 'break-all',
    },
    removeBtn: {
        position: 'absolute',
        top: 2,
        right: 2,
        padding: 2,
    },
    emptyHint: {
        padding: theme.spacing(1.5),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 6,
    },
}));

/** Backend shape: { [feature_key]: { mode: 'active'|'available', config?: {} } } */
export const normalizePacFeatures = (val) => {
    if (!val || typeof val !== 'object' || Array.isArray(val)) return {};
    const out = {};
    Object.entries(val).forEach(([key, entry]) => {
        if (entry === null || entry === undefined) return;
        if (typeof entry !== 'object') return;
        const mode = entry.mode;
        if (mode !== 'active' && mode !== 'available') return;
        const normalized = { mode };
        if (entry.config && typeof entry.config === 'object' && Object.keys(entry.config).length) {
            normalized.config = { ...entry.config };
        }
        out[key] = normalized;
    });
    return out;
};

const getCardStyle = (mode) => {
    if (mode === 'active') return CARD_STYLES.active;
    if (mode === 'available') return CARD_STYLES.available;
    return CARD_STYLES.default;
};

/**
 * Per-plan feature matrix editor. Features come from the backend catalog
 * (constants). For each feature choose a mode: Not in plan / Active / Available.
 */
const PacFeaturesPicker = ({ value, onChange, disabled, helperText }) => {
    const classes = useStyles();
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const map = normalizePacFeatures(value);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        listFeaturesService()
            .then((res) => {
                if (!mounted) return;
                setFeatures((res && res.data && res.data.features) ? res.data.features : []);
            })
            .catch(() => { if (mounted) setFeatures([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    const setMode = (key, mode) => {
        const next = { ...map };
        if (!mode) delete next[key];
        else {
            const prev = map[key];
            next[key] = (prev?.config && typeof prev.config === 'object')
                ? { mode, config: { ...prev.config } }
                : { mode };
        }
        onChange(next);
    };

    const addedKeys = Object.keys(map);
    const availableToAdd = features.filter((f) => !map[f.feature_key]);

    const handlePickFeature = (key) => {
        if (!key || disabled) return;
        setMode(key, 'active');
    };

    const resolveFeature = (key) => features.find((f) => f.feature_key === key);

    if (loading) {
        return (
            <Box display="flex" alignItems="center" style={{ gap: 8, padding: '8px 0' }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">Loading features…</Typography>
            </Box>
        );
    }
    if (features.length === 0) {
        return <Typography variant="body2" color="textSecondary">No features defined in the platform catalog.</Typography>;
    }

    return (
        <Box>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                Features in this package
            </Typography>

            {availableToAdd.length > 0 && (
                <Box className={classes.addBar}>
                    <Select
                        className={classes.addSelect}
                        displayEmpty
                        variant="outlined"
                        size="small"
                        value=""
                        disabled={disabled}
                        onChange={(e) => handlePickFeature(e.target.value)}
                    >
                        <MenuItem value=""><em>— Add a feature —</em></MenuItem>
                        {availableToAdd.map((f) => (
                            <MenuItem key={f.feature_key} value={f.feature_key}>{f.label}</MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {addedKeys.length === 0 ? (
                <Typography variant="body2" className={classes.emptyHint}>
                    No features added yet. Choose one from the dropdown above.
                </Typography>
            ) : (
                <Box className={classes.cardGrid}>
                    {addedKeys.map((key) => {
                        const f = resolveFeature(key);
                        const mode = map[key]?.mode || '';
                        const cardStyle = getCardStyle(mode);
                        const label = f?.label || key;

                        return (
                            <Box
                                key={key}
                                className={classes.card}
                                style={{ background: cardStyle.background, border: cardStyle.border }}
                            >
                                <IconButton
                                    className={classes.removeBtn}
                                    size="small"
                                    disabled={disabled}
                                    aria-label="Remove feature"
                                    onClick={() => setMode(key, '')}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>

                                <Box className={classes.cardHeader}>
                                    <Box>
                                        <Typography className={classes.cardTitle} style={{ color: cardStyle.titleColor }}>
                                            {label}
                                        </Typography>
                                        <Typography className={classes.cardKey}>{key}</Typography>
                                    </Box>
                                </Box>

                                <Select
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={mode}
                                    disabled={disabled}
                                    onChange={(e) => setMode(key, e.target.value)}
                                >
                                    {MODES.map((m) => (
                                        <MenuItem key={String(m.value)} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {helperText && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};

export default PacFeaturesPicker;
